import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import TollOutlinedIcon from '@mui/icons-material/TollOutlined';
import { useQuery } from '@tanstack/react-query';
import { resources } from '../../core/services';

interface ProjectTokenUsageProps {
  projectId: number;
}

function formatCompact(n: number): string {
  return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

function TokenStat({ label, value }: { label: string; value: number }) {
  return (
    <Tooltip title={`${value.toLocaleString()} tokens`} arrow>
      <Stack alignItems="center" sx={{ px: 0.75, minWidth: 32, cursor: 'default' }}>
        <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1.2 }}>
          {formatCompact(value)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9, lineHeight: 1 }}>
          {label}
        </Typography>
      </Stack>
    </Tooltip>
  );
}

/**
 * Compact token-usage readout for the project header: total LLM tokens spent
 * today, in the last 7 days, and in the last 30 days (see LlmRequest.totalTokens).
 */
export function ProjectTokenUsage({ projectId }: ProjectTokenUsageProps) {
  const query = useQuery({
    queryKey: ['project-token-usage', projectId],
    queryFn: () => resources.getProjectTokenUsage(projectId),
  });

  if (query.isLoading) return <Skeleton variant="rounded" width={110} height={30} />;
  if (!query.data) return null;

  const { today, lastWeek, lastMonth } = query.data;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 5,
        pl: 1,
        pr: 0.5,
        py: 0.25,
      }}
    >
      <TollOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.25 }} />
      <TokenStat label="Today" value={today} />
      <TokenStat label="7d" value={lastWeek} />
      <TokenStat label="30d" value={lastMonth} />
    </Box>
  );
}
